import { PlayerPositionsShortEnum } from "../../../User/enums";

import { CreateProfessionalJob, CreateTalentJob } from "../../types/createJobSchema";
import { HiringMission, JobStatusEnum, ApplyCriteriaEnum } from "../../enum/jobEnums";
import { UserRoleEnum } from "../../../User/enums";

export const formatProfessionalJobPayload = (payload: CreateProfessionalJob) => {
  const {
    jobName,
    position,
    jobMode,
    jobLocation,
    jobDescription,
    applyEnds,
    salaryAmount,
    salaryCurrency,
    salaryPer,
    isConfidential,
    howManyHiresNeeded,
    nationality,
    talentProfessionalLocation,
    verified,
    onlyTalentsWithTestimonials,
    profileCompletionAbove80,
    isLanguagesSpokenActive,
    languagesSpoken,
    isCertificateAndLicensesActive,
    certificateAndLicenses,
    totalCareerTransfers,
    isClubsWorkedInActive,
    clubsWorkedIn,
    isCountriesCoachedInActive,
    countriesCoachedIn,
    isCountriesScoutedInActive,
    countriesScoutedIn,
    willingnessToRelocate,
    introductoryVideo,
    isIntroductoryVideoActive,
    ...rest
  } = payload;

  return {
    ...rest,
    status: JobStatusEnum.underReview,
    hiringMission: HiringMission.PROFESSIONAL_RECRUITMENT,
    name: jobName,
    professionalRole: position,
    subRole: position === UserRoleEnum.OTHER ? payload.subRole : undefined,
    jobMode: jobMode,
    locationCountryIds: jobLocation?.map((c: any) => (typeof c === "object" ? c.value : c)),
    description: jobDescription,
    applyEndsAt: applyEnds instanceof Date ? applyEnds.toISOString() : undefined,
    compensation: {
      amount: salaryAmount * 100,
      currency: salaryCurrency,
      period: salaryPer,
      confidential: isConfidential,
    },
    advancedTargeting: {
      hiresNeeded: howManyHiresNeeded,
      nationalityIds: nationality?.map((c: any) => (typeof c === "object" ? c.value : c)),
      locationCountryIds: talentProfessionalLocation?.map((c: any) => (typeof c === "object" ? c.value : c)),
      verificationRule: verified ? "VERIFIED_ONLY" : undefined,
      onlyTalentsWithTestimonials,
      profileCompletionAbove80,
    },
    requirements: {
      languagesSpoken: isLanguagesSpokenActive,
      preferredLanguages: languagesSpoken,
      certificationsLicenses: isCertificateAndLicensesActive,
      preferredCertifications: certificateAndLicenses,
      willingToRelocate: willingnessToRelocate,
      ...(isClubsWorkedInActive ? { sportsEntitiesWorkedIn: true } : {}),
      ...(isClubsWorkedInActive
        ? {
            preferredSportsEntityIds: clubsWorkedIn
              ? clubsWorkedIn.map((entity: any) => (typeof entity === "object" && entity?.id ? entity.id : entity))
              : undefined,
          }
        : {}),
      totalCareerTransfers: Boolean(totalCareerTransfers) ? true : undefined,
      totalCareerTransfersCount: totalCareerTransfers,

      ...(isCountriesCoachedInActive ? { sportsEntitiesWorkedIn: true } : {}),
      ...(isCountriesCoachedInActive
        ? { clubsWorkedInCountryIds: countriesCoachedIn?.map((c: any) => (typeof c === "object" ? c.value : c)) }
        : {}),

      ...(isCountriesScoutedInActive ? { sportsEntitiesWorkedIn: true } : {}),
      ...(isCountriesScoutedInActive
        ? { clubsWorkedInCountryIds: countriesScoutedIn?.map((c: any) => (typeof c === "object" ? c.value : c)) }
        : {}),

      introVideo: isIntroductoryVideoActive,
      videoDescription: introductoryVideo?.description,
      videoLength: introductoryVideo?.length,
    },
  };
};

export const formatTalentJobPayload = (payload: CreateTalentJob) => {
  const {
    jobName,
    primaryPosition,
    secondaryPosition,
    jobMode,
    jobLocation,
    jobDescription,
    applyEnds,
    salaryAmount,
    salaryCurrency,
    salaryPer,
    isConfidential,
    howManyHiresNeeded,
    nationality,
    talentProfessionalLocation,
    [ApplyCriteriaEnum.onlyVerifiedFootballers]: onlyVerifiedFootballers,
    [ApplyCriteriaEnum.onlyWithTestimonials]: onlyTalentsWithTestimonials,
    [ApplyCriteriaEnum.profileCompletionAbove80]: profileCompletionAbove80,
    isLanguagesSpokenActive,
    languagesSpoken,
    isIntroductoryVideoActive,
    introductoryVideo,
    isClubsWorkedInActive,
    clubsWorkedIn,
    majorInjury,
    willingnessToRelocate,
    ...rest
  } = payload;

  return {
    ...rest,
    status: JobStatusEnum.published,
    hiringMission: HiringMission.TALENT_DISCOVERY,
    name: jobName,
    primaryPosition: PlayerPositionsShortEnum[primaryPosition],
    secondaryPosition: PlayerPositionsShortEnum[secondaryPosition],
    jobMode: jobMode,
    locationCountryIds: jobLocation?.map((c: any) => (typeof c === "object" ? c.value : c)),
    description: jobDescription,
    applyEndsAt: applyEnds instanceof Date ? applyEnds.toISOString() : undefined,
    compensation: {
      amount: salaryAmount * 100,
      currency: salaryCurrency,
      period: salaryPer,
      confidential: isConfidential,
    },
    advancedTargeting: {
      hiresNeeded: howManyHiresNeeded,
      nationalityIds: nationality?.map((c: any) => (typeof c === "object" ? c.value : c)),
      locationCountryIds: talentProfessionalLocation?.map((c: any) => (typeof c === "object" ? c.value : c)),
      verificationRule: onlyVerifiedFootballers ? "VERIFIED_ONLY" : undefined,
      onlyTalentsWithTestimonials,
      profileCompletionAbove80,
    },
    requirements: {
      languagesSpoken: isLanguagesSpokenActive,
      preferredLanguages: languagesSpoken,
      willingToRelocate: willingnessToRelocate,
      majorInjury,
      ...(isClubsWorkedInActive ? { sportsEntitiesWorkedIn: true } : {}),
      ...(isClubsWorkedInActive
        ? {
            preferredSportsEntityIds: clubsWorkedIn
              ? clubsWorkedIn.map((entity: any) => (typeof entity === "object" && entity?.id ? entity.id : entity))
              : undefined,
          }
        : {}),
      introVideo: isIntroductoryVideoActive,
      videoDescription: introductoryVideo?.description,
      videoLength: introductoryVideo?.length,
    },
  };
};
